import React, { useState } from "react"
import { navigate } from "gatsby"
import { AppFunctionComponent } from "../../../types"
import styled from "styled-components"
import { RawLink } from "../link/link.component"
import { Path } from "../../../navigation/constants/routes"
import useTranslationHook from "../../../hooks/use-translation.hook"
import mobileBackground from "../../../images/not-found-bg-mobile.png"
import desktopBackground from "../../../images/not-found-bg-desktop-new.jpg"

const NotFoundContainer = styled.div`
  min-height: calc(100vh - ${({ theme }) => theme.size.headerHeightMobile}px);
  width: 100%;
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background-image: url(${mobileBackground});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    background-image: url(${desktopBackground});
    min-height: calc(
      100vh - ${({ theme }) => theme.size.headerHeightDesktop}px - 40px
    );
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.2);
    z-index: 1;
  }
`

const ContentWrapper = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  max-width: 800px;
  padding: 2rem;
  margin-top: 100px;

  @media (min-width: ${({ theme }) => theme.breakpoint.desktopMedium}px) {
    margin-top: 20px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    padding: 1rem;
    max-width: 90%;
    margin-top: 0;
  }
`

const ErrorNumber = styled.h1`
  color: black;
  text-align: center;
  font-family: Facto;
  font-size: 100px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  margin-bottom: 10px;

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}px) {
    text-align: center;
    font-family: Facto;
    font-size: 56px;
    font-style: normal;
    font-weight: 400;
    line-height: 120%; /* 67.2px */
  }
`

const MainTitle = styled.h2`
  text-align: center;

  /* Heading/H1 */
  font-family: Facto;
  font-size: 37px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%; /* 44.4px */ /* 120px */

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}px) {
    text-align: center;

    /* Heading/H1 - Mobile */
    font-family: Facto;
    font-size: 30px;
    font-style: normal;
    font-weight: 400;
    line-height: 120%;
  }
`

const Subtitle = styled.p`
  text-align: center;

  /* Text/Regular/Medium */
  font-family: Montserrat;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  margin-bottom: 48px;

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}px) {
    text-align: center;

    font-family: Montserrat;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 150%; /* 21px */
  }
`

const SearchForm = styled.form`
  display: flex;
  gap: 10px;
  margin: 0 0 2rem 0;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}px) {
    flex-direction: column;
    gap: 1rem;
  }
`

const SearchInput = styled.input`
  flex: 1;
  padding: 0.75rem 1rem;
  border: 1px solid black;
  border-radius: 4px;
  font-size: 1rem;
  outline: none;
  background: transparent;

  &:focus {
    border-color: #333;
  }

  &::placeholder {
    color: black;
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}px) {
    border-radius: 4px;
    border: 1px solid #ccc;
  }
`

const SearchButton = styled.button`
  padding: 0.2rem 2rem;
  background: #333;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s ease;

  &:hover {
    background: #555;
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}px) {
    border-radius: 4px;
    padding: 0.8rem 2rem;
  }
`

const HelpText = styled.p`
  text-align: center;

  /* Text/Small/Semi Bold */
  font-family: Montserrat;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%; /* 21px */

  a {
    text-align: center;
    color: black;
    text-decoration: underline;
    /* Text/Small/Semi Bold */
    font-family: Montserrat;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 150%; /* 21px */

    &:hover {
      text-decoration: none;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}px) {
    font-size: 0.9rem;
  }
`

const MobileHelpText = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    display: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}px) {
    ${HelpText} {
      margin-bottom: 0.5rem;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
`

const DesktopHelpText = styled(HelpText)`
  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}px) {
    display: none;
  }
`

const NotFoundPage: AppFunctionComponent = () => {
  const { translate, language } = useTranslationHook()
  const [searchQuery, setSearchQuery] = useState("")

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`)
    }
  }

  return (
    <NotFoundContainer>
      <ContentWrapper>
        <ErrorNumber>{translate("404-number")}</ErrorNumber>
        <MainTitle>{translate("404-title")}</MainTitle>
        <Subtitle>{translate("404-subtitle")}</Subtitle>

        <SearchForm onSubmit={handleSearchSubmit}>
          <SearchInput
            type="text"
            placeholder={translate("404-search-placeholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <SearchButton type="submit">
            {translate("404-search-button")}
          </SearchButton>
        </SearchForm>

        {/* Desktop version - single line */}
        <DesktopHelpText>
          {translate("404-help-contact-home-desktop")
            .split(" | ")
            .map((part, index) => (
              <React.Fragment key={index}>
                {index === 0 ? (
                  <>
                    {
                      part.split(
                        language === "en" ? "Talk to us" : "Porozmawiaj z nami"
                      )[0]
                    }
                    <RawLink to={Path.Contact}>
                      {translate("404-contact-link")}
                    </RawLink>
                    {
                      part.split(
                        language === "en" ? "Talk to us" : "Porozmawiaj z nami"
                      )[1]
                    }
                  </>
                ) : (
                  <>
                    {" | "}
                    {
                      part.split(
                        language === "en" ? "home" : "wróć do strony głównej"
                      )[0]
                    }
                    <RawLink to={Path.Home}>
                      {language === "en" ? "home" : "wróć do strony głównej"}
                    </RawLink>
                    {
                      part.split(
                        language === "en"
                          ? "Or gently head home"
                          : "wróć do strony głównej"
                      )[1]
                    }
                  </>
                )}
              </React.Fragment>
            ))}
        </DesktopHelpText>

        {/* Mobile version - multiple lines */}
        <MobileHelpText>
          <HelpText>{translate("404-help-text")}</HelpText>
          <HelpText>
            <RawLink to={Path.Contact}>{translate("404-contact-link")}</RawLink>
          </HelpText>
          <HelpText>
            <RawLink to={Path.Home}>{translate("404-home-text")}</RawLink>
          </HelpText>
        </MobileHelpText>
      </ContentWrapper>
    </NotFoundContainer>
  )
}

export default NotFoundPage
